import { Merchant, Registration } from '@stax-core/types-omni';
import { pick, omitBy } from '../../../util/object.util';
import { AuthDatum } from '../../../context';
import { coreapi } from '../../../api';

export type StatusTabType = 'All' | 'Pending' | 'Active' | 'Inactive';

function trimEmptyValues(
  registration: Partial<Registration>,
): Partial<Registration> {
  // lenient equality operator (==) is purposeful here
  // as it will coerce undefined to null as well
  return omitBy(registration, value => value == null || value === '');
}

type CreateTeamResult = {
  authData: AuthDatum[];
  newRegistration: Registration;
};

export async function createTeam(
  authToken: string,
  currentMerchant: Merchant,
): Promise<CreateTeamResult> {
  // NOTE: this call returns a new auth set
  // that _includes_ the new merchant (at the top of the auth set)
  // it does not return the new merchant itself.
  const authData: AuthDatum[] = await coreapi.post(authToken, '/team', {
    company_name: 'Additional Location',
    contact_email: currentMerchant.contact_email,
    brand: currentMerchant.brand,
    is_payfac: currentMerchant.is_payfac,
    // Carry the parent's processor application through alongside `is_payfac` so
    // the new location takes the same signing path as its parent. Without it the
    // new merchant would have no processor application and fall through to the
    // ISO/HelloSign path regardless of `is_payfac`.
    ...(currentMerchant.merchant_processor_application
      ? {
          merchant_processor_application:
            currentMerchant.merchant_processor_application,
        }
      : {}),
    status: 'PENDING',
    is_additional_location: true,
  });

  // get the registration record for the current merchant
  const currentRegistration: Registration = await coreapi.get(
    authToken,
    '/team/registration',
  );

  // trim empty values from registration to prevent a possible 422
  // only copy over the internal pricing values and application info
  // since we are creating and not cloning
  const registrationPayload: Partial<Registration> = trimEmptyValues({
    ...pick(
      currentRegistration,
      'bus_type',
      'is_flat_rate',
      'plan_txamnt',
      'credit_mid_trans_fee',
      'credit_non_trans_fee',
      'credit_qual_trans_fee',
      'amex_qual_trans_fee',
      'amex_mid_trans_fee',
      'amex_non_trans_fee',
      'plan_dcamnt',
      'credit_qual_disc_rate',
      'credit_mid_disc_rate',
      'credit_non_disc_rate',
      'amex_qual_disc_rate',
      'amex_mid_disc_rate',
      'amex_non_disc_rate',
    ),
    meta: {
      ownership_percentage: '100',
    },
    registration_source: 'self-registration',
  });

  // token for the merchant we just created
  const newToken = authData[0].token;
  // update registration for new merchant
  const newRegistration = await coreapi.put(
    newToken,
    '/team/registration',
    registrationPayload,
  );

  return { authData, newRegistration };
}

export async function cloneTeam(
  authToken: string,
  selectedMerchant: Merchant,
): Promise<AuthDatum[]> {
  await coreapi.post(
    authToken,
    `/team/${selectedMerchant.id}/clone?withRegistration=true`,
    {
      registration_source: 'self-registration',
    },
  );

  return coreapi.get(authToken, '/self/list');
}

// New utility functions for grouped merchant functionality
export interface GroupedMerchants {
  All: AuthDatum[];
  Pending: AuthDatum[];
  Active: AuthDatum[];
  Inactive: AuthDatum[];
}

export interface TabCounts {
  All: number;
  Pending: number;
  Active: number;
  Inactive: number;
}

export const groupMerchantsByStatus = (
  authData: AuthDatum[],
): GroupedMerchants => {
  const grouped: GroupedMerchants = {
    All: [...authData],
    Pending: [],
    Active: [],
    Inactive: [],
  };

  authData.forEach(datum => {
    if (!datum.merchant) return;

    switch (datum.merchant.status) {
      case 'PENDING':
        grouped.Pending.push(datum);
        break;
      case 'ACTIVE':
        grouped.Active.push(datum);
        break;
      case 'INACTIVE':
        grouped.Inactive.push(datum);
        break;

      default:
        break;
    }
  });

  return grouped;
};

export const sortMerchantsByCreatedDate = (
  merchants: AuthDatum[],
): AuthDatum[] => {
  return [...merchants].sort((a, b) => {
    if (!a.merchant?.created_at || !b.merchant?.created_at) return 0;

    // Sort by created_at descending (newest first)
    return (
      new Date(b.merchant.created_at).getTime() -
      new Date(a.merchant.created_at).getTime()
    );
  });
};

export const getTabCounts = (grouped: GroupedMerchants): TabCounts => ({
  All: grouped.All.length,
  Pending: grouped.Pending.length,
  Active: grouped.Active.length,
  Inactive: grouped.Inactive.length,
});

export const getFilteredAndSortedMerchants = (
  authData: AuthDatum[],
  activeTab: StatusTabType,
): AuthDatum[] => {
  const grouped = groupMerchantsByStatus(authData);
  const filteredMerchants = grouped[activeTab];
  return sortMerchantsByCreatedDate(filteredMerchants);
};
